<script lang="ts" setup>
import { computed, nextTick, ref, type ComponentPublicInstance } from 'vue';
import InputTextBase from './InputTextBase.vue';
import { useValidator } from '@/composeables';
import type { IEditorComponent, ValidatorDef } from '@/objects/gui';
import { type IInputNullableFloatModelValueType, ValidatorMessage, type IInputNullableFloatPropsWithoutModelValue } from '@/components';
import { isCommonKey } from '@/utilities/keyboardutils';

const props = withDefaults(defineProps<IInputNullableFloatPropsWithoutModelValue>(), {
    emptyValue: null,
    validator: null,
});

const modelValue = defineModel<IInputNullableFloatModelValueType>({ required: true });

const { Validate, ValidateMessage } = useValidator(props.validator, modelValue.value);

const textValue = computed<string>({
    get() {
        return (modelValue.value ?? props.emptyValue)?.toString() || '';
    },
    set(value) {
        const num = parseFloat(value.replace(',', '.'));
        if (isNaN(num)) {
            modelValue.value = props.emptyValue;
        } else {
            modelValue.value = num;
        }
    },
});

const mismatch = async () => {
    // The text in the box wont change unless we change the modelValue, so we change it to -999 and then back to emptyValue
    if (modelValue.value == props.emptyValue) {
        modelValue.value = -999;
        await nextTick();
    }
    modelValue.value = props.emptyValue;
};

// see mismatch comment, this will give us a warning in console
if (props.emptyValue == -999) {
    console.warn('InputInt: emptyValue should not be -999, it will not update gui on pattern mismatch');
}

const inputref = ref<ComponentPublicInstance<IEditorComponent> | null>(null);

/**
 * We make sure that the key is a number or a comma or a dot
 * @param e
 */
const filterKeys = (e: KeyboardEvent) => {
    const inputvalue = (e.target as HTMLInputElement)?.value;
    // If the key is a dot or a comma and we already have one, we prevent the default
    if ((e.key === '.' || e.key === ',') && (inputvalue.includes('.') || inputvalue.includes(','))) {
        e.preventDefault();
    }

    // If the key is not a number or a comma or a dot, we prevent the default
    if (!/[\d,.]/.test(e.key) && !isCommonKey(e)) {
        e.preventDefault();
    }
};

defineExpose<IEditorComponent>({
    focus: () => inputref.value?.focus(),
    validateMessage: ValidateMessage,
    KeyboardEvent: (ev: KeyboardEvent) => {
        const input = ev.key;
        if (!isNaN(Number(input))) {
            textValue.value = input;
            return;
        }
        modelValue.value = null;
    },
});
</script>

<template>
    <ValidatorMessage :message="ValidateMessage">
        <div
            class="form-control-number input-group flex-nowrap"
            :class="[$attrs.class]"
        >
            <InputTextBase
                ref="inputref"
                v-model="textValue"
                class="text-end form-control"
                @keydown="filterKeys"
                @pattern-mismatch="mismatch"
                :pattern="'[0-9,.]*'"
                :placeholder="props.placeholder"
                :select-on-focus="true"
                :class="$attrs.class"
                :readonly="props.readonly"
                :disabled="props.disabled"
                :autofocus="props.autofocus"
                :icon="props.icon ?? 'fa-hashtag'"
                :icon-family="props.iconFamily"
                :id="props.id"
                :label="props.label"
                :label-position="props.labelPosition"
                :maxlength="props.maxlength"
                :mark-text-part="props.markTextPart"
                :modelModifiers="modelModifiers"
                :tooltip="props.tooltip"
                :posttext="props.posttext"
                type="number"
            />
        </div>
    </ValidatorMessage>
</template>
